import React, { useEffect, useState } from "react";
import { Button, Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { formatNumberToLocal, toastError } from "../../helpers";
import productImg from "../../assets/images/product-image.svg";
import { useNavigate, useParams } from "react-router";
import ApplyCouponCode from "../discounts/ApplyCouponCode";
import { useDispatch } from "react-redux";
import { setProductData } from "../../app/features/ProductSlice";
import { BANK_PAYMENT_METHODS, CURRENCY_SYMBOLS } from "../../Constants";

const HeliorPurchaseSummary = ({
  purchaseData,
  formData,
  setPurchaseData,
  eosKey,
  currency = "CHF",
  rate = 1,
}) => {
  const currencySymbol = CURRENCY_SYMBOLS[currency] ?? currency;
  const conv = (v) => formatNumberToLocal((v ?? 0) * rate) || 0;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { planId, lang } = useParams();
  const dispatch = useDispatch();
  const [enableCoupon, setEnableCoupon] = useState(false);

  const isBankPayment = BANK_PAYMENT_METHODS.includes(
    purchaseData.paymentMethod,
  );
  let txFee = 0;
  if (!isBankPayment) {
    txFee = purchaseData.txCryptoFee;
  } else {
    txFee =
      purchaseData.paymentMethod === "Credit Card"
        ? purchaseData.txFeeCC
        : purchaseData.txBankFee;
  }

  const validateData = (data) => {
    if (data.paymentAmount <= 0) {
      toastError("", t("recurringPaymentAmountZero"));
      return false;
    }
    if (
      data.recurringPaymentNumber < formData?.minRecurringPaymentNumber ||
      data.recurringPaymentNumber > formData?.maxRecurringPaymentNumber
    ) {
      toastError(
        "",
        `${t("minRecurringNumberMsg")} ${
          formData?.minRecurringPaymentNumber
        } ${t("and")} ${formData?.maxRecurringPaymentNumber}`,
      );
      return false;
    }
    if (data.initialPaymentNumber <= 0) {
      toastError("", t("initialPaymentNumberZero"));
      return false;
    }
    if (data.initialPaymentNumber >= parseInt(data.recurringPaymentNumber)) {
      toastError("", t("initialPaymentNumberError"));
      return false;
    }

    const mandatoryAccepted =
      data.P01 &&
      data.P02 &&
      data.P03 &&
      data.P04 &&
      data.P05 &&
      data.P06 &&
      data.P07 &&
      data.P08 &&
      data.C01 &&
      data.C02 &&
      data.C03;
    const blockchainAccepted = !data.B01 || (data.B02P && data.B03);

    if (!mandatoryAccepted || !blockchainAccepted) {
      toastError("", t("planPurchaseAcceptTNC"));
      return false;
    }

    if (!data.paymentMethod) {
      toastError("", t("paymentMethodRequired"));
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    const data = {
      ...purchaseData,
      planId,
      currency,
      exchangeRate: rate,
      acceptBlockchain: Boolean(purchaseData.B01 && purchaseData.B02P && purchaseData.B03),
    };

    if (validateData(data)) {
      dispatch(setProductData(data));
      navigate(`/${lang}/excelior-plan/purchase-confirmation`);
    }
  };

  useEffect(() => {
    setEnableCoupon(purchaseData.dossierOpeningCharge >= 2000);
  }, [purchaseData]);

  return (
    <>
      <div className="d-flex align-items-center gap-2">
        <Image src={productImg} className="img-fluid" alt="XPlan" />
        <h5 className="title fs-3">XPlan</h5>
      </div>
      <hr />
      <ul className="list-unstyled">
        <li className="d-flex justify-content-between mb-3">
          <span className="w-50 lh-sm">{t("recurringAmountPaid")} </span>
          <span className="text-nowrap">
            {currencySymbol} {conv(purchaseData.totalRecurringAmount)}
          </span>
        </li>
        <li className="d-flex justify-content-between mb-3">
          <span className="w-50 lh-sm">{t("numberOfInitialPayments2")} </span>
          <span className="text-nowrap">
            {purchaseData.initialPaymentNumber || 0}
          </span>
        </li>
        <li className="d-flex justify-content-between mb-3">
          <span className="w-50 lh-sm">{t("numberOfRecurringPayments2")} </span>
          <span className="text-nowrap">
            {purchaseData.recurringPaymentNumber || 0}
          </span>
        </li>
        <li className="d-flex justify-content-between mb-3">
          <span className="w-50 lh-sm">{t("paymentAmount")} </span>
          <span className="text-nowrap">
            {currencySymbol} {conv(purchaseData.recurringPaymentAmount)}
          </span>
        </li>
        <li className="d-flex justify-content-between mb-3">
          <span className="w-50 lh-sm">{t("docShareOfTotal")} </span>
          <span className="text-nowrap">
            {currencySymbol} {conv(purchaseData.dossierOpeningCharge)}
          </span>
        </li>
        {purchaseData.docSplit && (
          <li className="d-flex justify-content-between mb-3">
            <span className="w-50 lh-sm">{t("docSplitAmount")} </span>
            <span className="text-nowrap">
              {currencySymbol} {conv(purchaseData.docSplitAmount)}
            </span>
          </li>
        )}
        <li className="d-flex justify-content-between mb-3">
          <span className="w-50 lh-sm">{t("initialPaymentAmount")} </span>
          <span className="text-nowrap">
            {currencySymbol} {conv(purchaseData.initialPaymentAmount)}
          </span>
        </li>
        {enableCoupon && (
          <ApplyCouponCode
            purchaseData={purchaseData}
            setPurchaseData={setPurchaseData}
          />
        )}
      </ul>
      {purchaseData.totalPayment > 0 && (
        <ul className="list-unstyled">
          <hr />
          <li className="d-flex justify-content-between text-uppercase mb-1">
            <span className="w-50 lh-sm">{t("totalPayment")}</span>
            <span className="text-nowrap">
              {currencySymbol} {conv(purchaseData.totalPayment)}
            </span>
          </li>
          {purchaseData?.stampDuty && purchaseData?.stampDuty > 0 ? (
            <li className="d-flex justify-content-between text-uppercase mb-1">
              <span className="w-50 lh-sm">{t("stampDuty")}</span>
              <span className="text-nowrap">
                {currencySymbol} {conv(purchaseData?.stampDuty)}
              </span>
            </li>
          ) : (
            ""
          )}
          <li className="d-flex justify-content-between text-uppercase mb-1">
            <span>{t("paymentMethod")}</span>{" "}
            <span className="text-nowrap">
              {t(`${purchaseData.paymentMethod}`)}
            </span>
          </li>
          <hr />
          <li className="d-flex justify-content-between text-uppercase mb-2">
            <span>{t("totalPayment")}</span>
            <span className="text-nowrap">
               {currencySymbol}{" "}
              {conv(purchaseData.totalPayment + (purchaseData?.stampDuty || 0))}
            </span>
          </li>
          {purchaseData.paymentMethod && (
            <li className="d-flex justify-content-between text-uppercase mb-3">
              <span className="w-50 w-lg-100 lh-sm">
                {t("totalPurchaseWith")}
                {lang === "en" &&
                  `${BANK_PAYMENT_METHODS.includes(purchaseData.paymentMethod) 
                    ? purchaseData.paymentMethod?.toLowerCase() === 'credit card' ? purchaseData.txFeeCC : purchaseData.txBankFee
                    : purchaseData.txCryptoFee}%`}
                {t("TXFee")}
                {lang === "it" &&
                  `${BANK_PAYMENT_METHODS.includes(purchaseData.paymentMethod) 
                    ? purchaseData.paymentMethod?.toLowerCase() === 'credit card' ? purchaseData.txFeeCC : purchaseData.txBankFee
                    : purchaseData.txCryptoFee}%`}
              </span>
              <span className="text-nowrap">
                {currencySymbol}{" "}
                {conv(
                  purchaseData.totalPaymentWithTX +
                    (purchaseData?.stampDuty || 0),
                )}
              </span>
            </li>
          )}
          <Button
            variant="primary-outline"
            className="w-100 d-flex align-items-center justify-content-center mt-5"
            onClick={handleSubmit}
          // disabled={!eosKey?.isAvailable}
          >
            {purchaseData.docSplit && purchaseData.dossierOpeningCharge <= 12000
              ? t("sendRequest")
              : t("buyPlan")}
          </Button>
        </ul>
      )}
    </>
  );
};

export default HeliorPurchaseSummary;